/** Recherche d'articles partagée (fiches, stock, association d'un code-barres). */

const normalize = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Chaque mot saisi doit se retrouver dans le nom ou le code-barres, sans tenir compte des accents. */
export const matchesProductSearch = (
  product: { name: string; barcode?: string | null; alternate_barcodes?: string[] | null },
  query: string,
): boolean => {
  const codes = [product.barcode ?? '', ...(product.alternate_barcodes ?? [])].join(' ');
  const haystack = `${normalize(product.name)} ${codes}`;
  return normalize(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
};

type WithBarcodes = { barcode?: string | null; alternate_barcodes?: string[] | null };

/** Article portant ce code : le code principal passe avant les codes secondaires. */
export const findByBarcode = <T extends WithBarcodes>(products: T[], code: string): T | undefined =>
  products.find((p) => p.barcode === code) ??
  products.find((p) => p.alternate_barcodes?.includes(code));

/** Le nouveau code devient principal, l'ancien reste reconnu : les étiquettes collées scannent encore. */
export const barcodesAfterAssociation = (
  product: WithBarcodes,
  code: string,
): { barcode: string; alternate_barcodes: string[] } => {
  const previous = [product.barcode, ...(product.alternate_barcodes ?? [])];
  const kept = previous.filter((c): c is string => !!c && c !== code);
  return { barcode: code, alternate_barcodes: [...new Set(kept)] };
};

/** Filtre PostgREST : code principal ou secondaire, valeur nettoyée car elle vient du scanner. */
export const barcodeFilter = (barcode: string): string => {
  const clean = barcode.replace(/["\\{},()]/g, '');
  return `barcode.eq."${clean}",alternate_barcodes.cs.{"${clean}"}`;
};

export const looksLikeBarcode = (value: string): boolean => /^\d{8,14}$/.test(value.trim());

/** Message lisible à la place de l'erreur brute de l'index unique des codes-barres. */
export const friendlyBarcodeError = (message: string): string =>
  message.includes('idx_products_barcode_tenant')
    ? 'Ce code-barres est déjà utilisé par un autre article.'
    : message;
